/**
 * SESSION MANAGEMENT SERVICE
 * 
 * RESTRICTIONS:
 * - Follow LOGIN_FLOW_DOCUMENTATION.md session specifications exactly
 * - No logic changes to existing session handling
 * - Maintain all security features: IP binding, device fingerprinting, email binding
 * - 15-minute TTL with auto-renewal as documented
 * - Session regeneration after login as specified
 */

class SessionManager {
  constructor() {
    this.sessionId = null
    this.brandId = null
    this.email = null
    this.middlewareUrl = 'https://devhub.rezcomm.com/hubapmydbauth/mmb/middleware.php'
  }

  /**
   * Initialize session from localStorage
   */
  init() {
    this.sessionId = localStorage.getItem('sessionId')
    this.brandId = localStorage.getItem('brandId')
    this.email = localStorage.getItem('userEmail')
  }

  /**
   * Create new session (called after email verification)
   */
  createSession(sessionId, brandId, email) {
    this.sessionId = sessionId
    this.brandId = brandId
    this.email = email
    
    localStorage.setItem('sessionId', sessionId)
    localStorage.setItem('brandId', brandId)
    localStorage.setItem('userEmail', email)
  }

  /**
   * Update session after login (session regeneration)
   */
  updateSession(newSessionId) {
    this.sessionId = newSessionId
    localStorage.setItem('sessionId', newSessionId)
  }

  /**
   * Clear session data
   */
  clearSession() {
    this.sessionId = null
    this.brandId = null
    this.email = null
    
    localStorage.removeItem('sessionId')
    localStorage.removeItem('brandId')
    localStorage.removeItem('userEmail')
  }

  /**
   * Check if session exists
   */
  hasSession() {
    return !!this.sessionId
  }

  /**
   * Get current session data
   */
  getSessionData() {
    return {
      sessionId: this.sessionId,
      brandId: this.brandId,
      email: this.email
    }
  }

  /**
   * Validate session with middleware
   */
  async validateSession() {
    if (!this.hasSession()) {
      return { valid: false, error: 'NO_SESSION' }
    }

    try {
      const response = await fetch(this.middlewareUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'validate-session',
          brand_id: this.brandId,
          sessionId: this.sessionId,
          email_id: this.email,
          device_fingerprint: this.getDeviceFingerprint()
        })
      })

      const data = await response.json()
      
      if (!data.valid) {
        this.clearSession()
      }
      
      return data
    } catch (error) {
      console.error('Session validation error:', error)
      this.clearSession()
      return { valid: false, error: 'VALIDATION_ERROR' }
    }
  }

  /**
   * Generate device fingerprint for session security
   */
  getDeviceFingerprint() {
    const screen = `${window.screen.width}x${window.screen.height}`
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const userAgent = navigator.userAgent
    return btoa(`${userAgent}|${screen}|${timezone}`)
  }

  /**
   * Auto-renewal mechanism (called periodically)
   */
  async renewSession() {
    if (!this.hasSession()) {
      return false
    }

    const validation = await this.validateSession()
    return validation.valid
  }
}

// Export singleton instance
export default new SessionManager()
